import React from 'react';
import Popover from 'react-popover';

import FlatButton from '../FlatButton';
import ArrowRightIcon from '../Icons/ArrowRightIcon';
import AttentionIcon from '../Icons/AttentionIcon';
import StarIcon from '../Icons/StarIcon';
import RadarChart from '../Charts/Radar';
import Spinner from '../LoadingSpinner';
import TestItemIcon from '../TestItemIcon';

import useFetch from '../../hooks/useFetch';
import {getAreaIcon, roundToFirstDecimal} from '../../services/utilities';

import s from './IndividualReport.css';
import cl from 'classnames';

export default function IndividualReport({userId, campaignId, changeMode, report}) {
  return (
    <div className={s.ReportIndividualContainer}>
      <IndividualControllers campaignId={campaignId} userId={userId} changeMode={changeMode} />
      <AreasOverview
        areas={report.areas.filter(area => area.id !== 7 && area.id !== 9 && area.id !== 6)}
      />
      <DetailsContainer report={report} />
    </div>
  );
}

const TestsWithTasksContext = React.createContext(null);

function DetailsContainer({report}) {
  const {areas, skills, markers, tasks, tests} = report;

  //DENORMALIZING ALL THE DATA STRUCTURE TO EASILY RENDER
  const markersPerArea = areas.map(area => {
    const foundSkills = skills.filter(skill => skill.areaId === area.id);

    const markerPerSkill = foundSkills.map(skill => {
      const foundMarkers = markers.filter(marker => marker.skillId === skill.id);

      const taskPerMarker = foundMarkers.map(marker => {
        const foundTasks = tasks.filter(task => task.markerId === marker.id);

        return {...marker, tasks: foundTasks};
      });

      return {...skill, markers: taskPerMarker};
    });

    return {...area, skills: markerPerSkill};
  });

  //DENORMALIZING TESTS AND RELATED MARKERS
  const tempTest = new Map();

  const tasksPerTest = tests
    .filter(test => {
      if (tempTest.has(test.id)) {
        tempTest.get(test.id).add(test.taskId);
        return false;
      } else {
        tempTest.set(test.id, new Set().add(test.taskId));
        return true;
      }
    })
    .map(test => ({...test, tasks: tempTest.get(test.id)}));

  const worstTwoSkills = new Set(
    skills
      .filter(skill => parseFloat(skill.value, 10) < 3)
      .sort((skillA, skillB) => parseFloat(skillA.value, 10) - parseFloat(skillB.value, 10))
      .slice(0, 2)
      .map(skill => skill.id)
  );

  const bestTwoSkills = new Set(
    skills
      .filter(skill => parseFloat(skill.value, 10) > 3)
      .sort((skillA, skillB) => parseFloat(skillB.value, 10) - parseFloat(skillA.value, 10))
      .slice(0, 2)
      .map(skill => skill.id)
  );

  return (
    <TestsWithTasksContext.Provider value={tasksPerTest}>
      <div className={s.DetailsContainer}>
        <h2>Risultati in dettaglio</h2>
        <div className={s.InteractivePlayground}>
          <div className={s.Header}>
            <div className={s.ColumnOne}>AREE</div>
            <div className={s.ColumnTwo}>COMPETENZE</div>
          </div>
        </div>
        <ul className={s.AreasContainer}>
          {markersPerArea.map(area => (
            <AreaListItem
              key={area.id}
              area={area}
              worstTwoSkills={worstTwoSkills}
              bestTwoSkills={bestTwoSkills}
            />
          ))}
        </ul>
      </div>
    </TestsWithTasksContext.Provider>
  );
}

function AreaListItem({area, worstTwoSkills, bestTwoSkills}) {
  const AreaIcon = getAreaIcon(area.id);

  const {skills} = area;

  return (
    <li className={s.ListItem}>
      <div className={s.ColumnOne}>
        <AreaIcon width={'3em'} height={'4em'} />
      </div>
      <SkillList bestTwoSkills={bestTwoSkills} worstTwoSkills={worstTwoSkills} skills={skills} />
    </li>
  );
}

function SkillList({skills, worstTwoSkills, bestTwoSkills}) {
  return (
    <ul className={cl(s.ColumnTwo, s.Skills)}>
      {skills.map(skill => (
        <SkillListItem
          key={skill.id}
          skill={skill}
          suggestionsNeeded={worstTwoSkills.has(skill.id)}
          starNeeded={bestTwoSkills.has(skill.id)}
          worstTwoSkills={worstTwoSkills}
        />
      ))}
    </ul>
  );
}

function SkillListItem({skill, suggestionsNeeded, worstTwoSkills, starNeeded}) {
  const [showMarkers, setShowmarkers] = React.useState(false);
  const [isHoveringFeedback, setIsHoveringFeedback] = React.useState(false);

  const scrollToLi = React.useRef();

  function onSkillNameClick() {
    setShowmarkers(showMarkers => !showMarkers);
  }

  function showFeedbackIconHover() {
    setIsHoveringFeedback(true);
  }

  function hideFeedbackIconHover() {
    setIsHoveringFeedback(false);
  }

  React.useEffect(() => {
    if (showMarkers) scrollToLi.current.scrollIntoView({behavior: 'smooth'});
  }, [showMarkers]);

  const {markers} = skill;

  return (
    <li className={showMarkers ? cl(s.RowContainer, s.RowContainerSelected) : s.RowContainer}>
      <div key={skill.id} className={s.SkillBar} onClick={onSkillNameClick}>
        <div className={showMarkers ? cl(s.SkillName, s.PaddingSelected) : s.SkillName}>
          <span>{skill.name}</span>
        </div>
        <div
          className={s.FeedbackIcon}
          onMouseOver={showFeedbackIconHover}
          onMouseLeave={hideFeedbackIconHover}
        >
          {starNeeded ? <StarIcon /> : null}
          {suggestionsNeeded ? (
            <Popover
              tipSize={'.01'}
              isOpen={isHoveringFeedback}
              body={<DevSuggestion worstTwoSkills={worstTwoSkills} markers={markers} />}
            >
              <AttentionIcon />
            </Popover>
          ) : null}
        </div>
        <ScoreBar score={roundToFirstDecimal(parseFloat(skill.value))} />
      </div>
      {showMarkers ? (
        <div className={s.MarkersSection}>
          <div className={cl(s.MarkersHeader, s.PaddingSelected)}>indicatori</div>
          <ul>
            {markers.map(marker => (
              <MarkersSection key={marker.id} marker={marker} />
            ))}
            <li ref={scrollToLi} />
          </ul>
        </div>
      ) : null}
    </li>
  );
}

function DevSuggestion({worstTwoSkills, markers}) {
  const worstTwoMarkers = markers
    .filter(marker => worstTwoSkills.has(marker.skillId))
    .sort((markerA, markerB) => parseFloat(markerA.value, 10) - parseFloat(markerB.value, 10))
    .slice(0, 2);

  return (
    <div className={s.DevSuggestion}>
      <h4>Punto di attenzione</h4>
      <div>Suggerimenti per lo sviluppo:</div>
      <ol>
        {worstTwoMarkers.map(marker => (
          <li key={marker.id}>
            <div>{marker.devSuggestion.split('---')[0]}</div>
          </li>
        ))}
      </ol>
    </div>
  );
}

function MarkersSection({marker}) {
  const [showFeedbackTasks, setShowFeedbackTasks] = React.useState(false);

  function onMarkerClick() {
    setShowFeedbackTasks(showFeedback => !showFeedback);
  }

  return (
    <li
      key={marker.id}
      className={
        showFeedbackTasks
          ? cl(s.RowContainer, s.RowContainerSelected, s.MarkerSelected)
          : s.RowContainer
      }
    >
      <div className={cl(s.SkillBar, s.MarkerBar)} onClick={onMarkerClick}>
        <div className={cl(s.SkillName, s.MarkerName, s.PaddingSelected)}>
          <span style={showFeedbackTasks ? {fontWeight: 500} : null}>{marker.name}</span>
        </div>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            width: '60%',
            marginLeft: '8%',
          }}
        >
          <ScoreBar
            containerStyle={{width: '100%'}}
            height={'1.2em'}
            score={roundToFirstDecimal(parseFloat(marker.value))}
          />
          {showFeedbackTasks ? <MarkerFeedback marker={marker} /> : null}
        </div>
      </div>
    </li>
  );
}

function MarkerFeedback({marker}) {
  const testsWithTasks = React.useContext(TestsWithTasksContext);

  const testsInMarker = testsWithTasks.filter(test => {
    const {tasks: markerTasks} = marker;
    const {tasks: testTasks} = test;

    return markerTasks.some(task => testTasks.has(task.id));
  });

  return (
    <div className={s.FeedbackContainer}>
      <div className={s.FeedbackColumn}>
        <div className={s.MarkersHeader}>valutazione in dettaglio</div>
        <div className={s.MarkerFeedback}>{marker.feedback}</div>
      </div>
      <div className={s.FeedbackColumn}>
        <div className={s.MarkersHeader}>prove legate all'indicatore</div>
        <div>
          {testsInMarker.map(test => (
            <div key={test.id} className={s.Test}>
              <div style={{width: '35px', height: '35px', marginRight: '0.5em'}}>
                <TestItemIcon iconLabel={test.abbreviation} size={35} />
              </div>
              <div>{test.name}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ScoreBar({score, containerStyle, height}) {
  const barWidth = {
    width: `${score * 20}%`,
    borderTopRightRadius: score >= 5 ? '20px' : '0',
    borderBottomRightRadius: score >= 5 ? '20px' : '0',
  };

  return (
    <div
      style={{
        width: '55%',
        display: 'flex',
        alignItems: 'start',
        ...containerStyle,
        height,
      }}
    >
      <div style={{width: '10%'}}>{score}</div>
      <div className={s.ScoreBar} style={height ? {height, marginTop: '-3px'} : null}>
        <div style={barWidth} className={s.Bar} />
      </div>
    </div>
  );
}

function IndividualControllers({campaignId, userId, changeMode}) {
  return (
    <div className={s.IndividualControllers}>
      <a href={`/api/report/${campaignId}/individual/hr/${userId}/download`} download>
        Scarica il report
      </a>
    </div>
  );
}

function AreasOverview({areas}) {
  return (
    <div className={s.Report}>
      <div className={s.CommentContainer}>
        <h2>Sintesi qualitativa per profilo di area</h2>
        <h4>Commento</h4>
        <p>{areas.map(area => area.feedback).join(' ')}</p>
      </div>
      <div className={s.ChartContainer}>
        <RadarChart areas={areas} />
      </div>
    </div>
  );
}
